import { useEffect, useState } from "react";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import { WaveformBars } from "../chat/WaveformBars";
import { formatDuration } from "../../../utils";

// Reuses useVoiceRecorder.ts entirely as-is (MIME fallback chain,
// AnalyserNode waveform, start/stop/discard state machine - all fully
// generic, confirmed nothing chat-specific inside the hook itself). Only
// the UI is new: a simple tap-to-record button, not VoiceRecorderBar.tsx's
// WhatsApp-style hold-to-record/slide-to-cancel gesture - a Voice Drop is
// composed deliberately, not dashed off mid-conversation, so the brief's
// own "this is NOT a normal chat voice note" framing calls for the
// simpler interaction here.
export function VoiceDropRecorder({
  onRecorded,
  onCancel,
}: {
  onRecorded: (file: File, durationSec: number, waveform: number[]) => void;
  onCancel: () => void;
}) {
  const recorder = useVoiceRecorder();
  const { state } = recorder;
  const [playing, setPlaying] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (state.phase !== "stopped" || !state.finalBlob) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(state.finalBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [state.phase, state.finalBlob]);

  useEffect(() => {
    if (state.phase !== "error" || !state.errorMessage) return;
    // Same toast-and-reset pattern VoiceRecorderBar.tsx already uses for
    // this exact error state - no dedicated fallback UI exists for mic
    // denial anywhere in the app yet, so this stays consistent with the
    // one place that does handle it today.
    const timer = setTimeout(() => recorder.reset(), 3000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase]);

  function handleUse(): void {
    if (!state.finalBlob || state.finalDurationSec === null) return;
    const ext = state.finalExt ?? ".webm";
    const file = new File([state.finalBlob], `voice-drop-${Date.now()}${ext}`, { type: state.finalBlob.type });
    onRecorded(file, state.finalDurationSec, state.finalWaveform ?? []);
  }

  if (state.phase === "stopped" && previewUrl) {
    return (
      <div className="private-drop-voice-recorder">
        <audio
          src={previewUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          id="privateDropVoicePreview"
        />
        <button
          type="button"
          className="private-drop-voice-play"
          aria-label={playing ? "Pause" : "Play"}
          onClick={() => {
            const el = document.getElementById("privateDropVoicePreview") as HTMLAudioElement | null;
            if (!el) return;
            if (playing) el.pause();
            else el.play();
          }}
        >
          <i className={`bi ${playing ? "bi-pause-fill" : "bi-play-fill"}`} aria-hidden="true" />
        </button>
        <WaveformBars levels={state.finalWaveform ?? []} />
        <span className="private-drop-voice-duration">{formatDuration(state.finalDurationSec ?? 0)}</span>
        <div className="private-drop-voice-actions">
          <button type="button" className="btn btn-outline-pink btn-sm" onClick={() => recorder.reset()}>
            Re-record
          </button>
          <button type="button" className="btn btn-pink btn-sm" onClick={handleUse}>
            Use this recording
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="private-drop-voice-recorder">
      {state.phase === "error" ? (
        <p className="private-drop-voice-error">{state.errorMessage}</p>
      ) : state.phase === "recording" ? (
        <>
          <WaveformBars levels={state.liveLevels} />
          <span className="private-drop-voice-duration">{formatDuration(state.elapsedMs / 1000)}</span>
        </>
      ) : (
        <p className="private-drop-voice-hint">Tap to start recording</p>
      )}
      <div className="private-drop-voice-controls">
        <button type="button" className="private-drop-capture-cancel" aria-label="Cancel" onClick={onCancel}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
        {state.phase !== "error" && (
          <button
            type="button"
            className={`private-drop-capture-shutter${state.phase === "recording" ? " is-recording" : ""}`}
            aria-label={state.phase === "recording" ? "Stop recording" : "Start recording"}
            onClick={() => (state.phase === "recording" ? recorder.stop() : recorder.start())}
          />
        )}
      </div>
    </div>
  );
}
